"use client"

import { useContext, useEffect, useMemo, useState } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import AutorenewIcon from "@mui/icons-material/Autorenew"
import EditIcon from "@mui/icons-material/Edit"
import { ThemeProvider } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import CustomPagination from "../transaccionComponent/CustomPagination"
import ModalRrhhAsistencia from "./ModalRrhhAsistencia.js"
import {
  RRHH_ESTADOS_ASISTENCIA,
  extractErrorMessage,
  formatDuracionMinutos,
  formatFecha,
  formatFechaHora,
  getEmpleadoNombreCompleto,
  getEstadoAsistenciaMeta,
  rrhhTheme,
} from "./rrhhUi.js"

const PAGE_SIZE = 10

const toDateInputValue = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10)

const TablaRrhhAsistenciaComponent = () => {
  const {
    ubicaciones,
    getUbicacion,
    getRrhhAsistencias,
    updateRrhhAsistencia,
    postRrhhRecalcularAsistencia,
  } = useContext(ErpContext)

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [recalculando, setRecalculando] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const [searchText, setSearchText] = useState("")
  const [estadoFilter, setEstadoFilter] = useState(null)
  const [ubicacionFilter, setUbicacionFilter] = useState(null)
  const [fechaDesde, setFechaDesde] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 6)
    return toDateInputValue(date)
  })
  const [fechaHasta, setFechaHasta] = useState(() => toDateInputValue(new Date()))

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedAsistencia, setSelectedAsistencia] = useState(null)

  const loadCatalogos = async () => {
    if (ubicaciones.length === 0) {
      await getUbicacion()
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await getRrhhAsistencias({
        page,
        limit: PAGE_SIZE,
        search: searchText.trim() || undefined,
        fechaDesde,
        fechaHasta,
        estadoAsistencia: estadoFilter?.value || undefined,
        idUbicacion: ubicacionFilter?.id || undefined,
      })

      setRows(response?.data || [])
      setTotalPages(response?.totalPages || 1)
      setTotalCount(response?.total || 0)
      setError(null)
    } catch (loadError) {
      setError(extractErrorMessage(loadError, "No se pudieron cargar las asistencias RRHH."))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCatalogos()
  }, [])

  useEffect(() => {
    loadData()
  }, [page, searchText, estadoFilter, ubicacionFilter, fechaDesde, fechaHasta])

  const handleRefresh = async () => {
    setError(null)
    setSuccess(null)
    await Promise.all([loadCatalogos(), loadData()])
  }

  const handleOpenEdit = (row) => {
    setSelectedAsistencia(row)
    setModalOpen(true)
  }

  const handleSaveAsistencia = async (payload) => {
    if (!selectedAsistencia?.id) return

    setSaving(true)
    try {
      await updateRrhhAsistencia(selectedAsistencia.id, { asistencia: payload })
      setSuccess("Asistencia actualizada correctamente.")
      setModalOpen(false)
      await loadData()
    } catch (saveError) {
      setError(extractErrorMessage(saveError, "No se pudo actualizar la asistencia."))
    } finally {
      setSaving(false)
    }
  }

  const handleRecalculateRange = async () => {
    setRecalculando(true)
    try {
      const response = await postRrhhRecalcularAsistencia({ fechaDesde, fechaHasta })
      setSuccess(
        `Recálculo completado. Creadas: ${response?.resumen?.creadas || 0}, actualizadas: ${response?.resumen?.actualizadas || 0}, omitidas: ${response?.resumen?.omitidas || 0}.`
      )
      await loadData()
    } catch (recalcError) {
      setError(extractErrorMessage(recalcError, "No se pudo recalcular la asistencia."))
    } finally {
      setRecalculando(false)
    }
  }

  const handleRecalculateRow = async (row) => {
    try {
      const response = await postRrhhRecalcularAsistencia({
        idEntidad: row.idEntidad,
        fecha: row.fecha,
      })
      setSuccess(
        `Asistencia recalculada. Creadas: ${response?.resumen?.creadas || 0}, actualizadas: ${response?.resumen?.actualizadas || 0}.`
      )
      await loadData()
    } catch (recalcError) {
      setError(extractErrorMessage(recalcError, "No se pudo recalcular la fila seleccionada."))
    }
  }

  const requiereRevisionCount = useMemo(
    () => rows.filter((row) => row.requiereRevision === true).length,
    [rows]
  )

  const cerradasCount = useMemo(
    () => rows.filter((row) => row.cerrado === true).length,
    [rows]
  )

  const columns = [
    {
      field: "fecha",
      headerName: "Fecha",
      minWidth: 120,
      flex: 0.75,
      valueFormatter: (value) => formatFecha(value),
    },
    {
      field: "empleado",
      headerName: "Empleado",
      minWidth: 220,
      flex: 1.4,
      valueGetter: (_, row) => getEmpleadoNombreCompleto(row),
    },
    {
      field: "legajo",
      headerName: "Legajo",
      minWidth: 120,
      flex: 0.8,
      valueGetter: (_, row) => row.entidad?.rrhhEmpleado?.legajo || "-",
    },
    {
      field: "estadoAsistencia",
      headerName: "Estado",
      minWidth: 140,
      flex: 0.85,
      renderCell: ({ value }) => {
        const meta = getEstadoAsistenciaMeta(value)
        return (
          <Chip
            label={meta.label}
            size="small"
            sx={{
              backgroundColor: meta.backgroundColor,
              color: meta.color,
              fontWeight: 700,
            }}
          />
        )
      },
    },
    {
      field: "turno",
      headerName: "Turno",
      minWidth: 160,
      flex: 1,
      valueGetter: (_, row) => row.turno?.descripcion || "-",
    },
    {
      field: "primerFichajeAt",
      headerName: "Primer fichaje",
      minWidth: 165,
      flex: 1,
      valueFormatter: (value) => formatFechaHora(value),
    },
    {
      field: "ultimoFichajeAt",
      headerName: "Último fichaje",
      minWidth: 165,
      flex: 1,
      valueFormatter: (value) => formatFechaHora(value),
    },
    {
      field: "minutosTrabajados",
      headerName: "Trabajado",
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (value) => formatDuracionMinutos(value),
    },
    {
      field: "minutosTarde",
      headerName: "Tarde",
      minWidth: 110,
      flex: 0.7,
      valueFormatter: (value) => formatDuracionMinutos(value),
    },
    {
      field: "minutosExtra",
      headerName: "Extra",
      minWidth: 110,
      flex: 0.7,
      valueFormatter: (value) => formatDuracionMinutos(value),
    },
    {
      field: "cerrado",
      headerName: "Cierre",
      minWidth: 120,
      flex: 0.75,
      renderCell: ({ row }) => (
        <Chip
          label={row.cerrado ? "Cerrada" : "Abierta"}
          size="small"
          sx={{
            backgroundColor: row.cerrado ? alpha("#22c55e", 0.14) : alpha("#64748b", 0.14),
            color: row.cerrado ? "#166534" : "#475569",
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar asistencia">
            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recalcular fila">
            <IconButton size="small" onClick={() => handleRecalculateRow(row)}>
              <AutorenewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]

  return (
    <ThemeProvider theme={rrhhTheme}>
      <Stack spacing={2.5}>
        <Paper sx={{ p: 2.5 }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
          >
            <Box>
              <Typography variant="h5" sx={{ color: "#1f2937", fontWeight: 700 }}>
                Asistencia diaria
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                Consolidación diaria calculada desde turnos y fichajes, con cierre manual y recálculo por rango o por fila.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                Actualizar
              </Button>
              <Button variant="contained" startIcon={<AutorenewIcon />} onClick={handleRecalculateRange} disabled={recalculando}>
                {recalculando ? "Recalculando..." : "Recalcular rango"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {(error || success) && (
          <Stack spacing={1}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}
          </Stack>
        )}

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
            <TextField
              label="Buscar empleado o legajo"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value)
                setPage(1)
              }}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Autocomplete
              options={RRHH_ESTADOS_ASISTENCIA}
              value={estadoFilter}
              onChange={(_, value) => {
                setEstadoFilter(value)
                setPage(1)
              }}
              sx={{ minWidth: 220 }}
              renderInput={(params) => <TextField {...params} label="Estado" />}
            />

            <Autocomplete
              options={ubicaciones}
              value={ubicacionFilter}
              onChange={(_, value) => {
                setUbicacionFilter(value)
                setPage(1)
              }}
              getOptionLabel={(option) => option.descripcion || String(option.id)}
              sx={{ minWidth: 220 }}
              renderInput={(params) => <TextField {...params} label="Ubicación" />}
            />

            <TextField
              label="Fecha desde"
              type="date"
              value={fechaDesde}
              onChange={(event) => {
                setFechaDesde(event.target.value)
                setPage(1)
              }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Fecha hasta"
              type="date"
              value={fechaHasta}
              onChange={(event) => {
                setFechaHasta(event.target.value)
                setPage(1)
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Chip
              label={`Revisión pendiente: ${requiereRevisionCount}`}
              sx={{
                backgroundColor: alpha("#fb923c", 0.18),
                color: "#7c2d12",
                fontWeight: 700,
              }}
            />
            <Chip
              label={`Cerradas: ${cerradasCount}`}
              sx={{
                backgroundColor: alpha("#22c55e", 0.14),
                color: "#166534",
                fontWeight: 700,
              }}
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 1.5 }}>
          <Box sx={{ width: "100%" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              loading={loading}
              hideFooter
              pageSizeOptions={[PAGE_SIZE]}
            />
          </Box>

          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            vieneDe="tablaRrhhAsistencia"
            onPageChange={(_, nextPage) => setPage(nextPage)}
          />
        </Paper>
      </Stack>

      <ModalRrhhAsistencia
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        onSubmit={handleSaveAsistencia}
        asistencia={selectedAsistencia}
        submitting={saving}
      />
    </ThemeProvider>
  )
}

export default TablaRrhhAsistenciaComponent
